/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshControl, ScrollView, StyleSheet, Text, View } from 'react-native';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame'
import { useTabWorkspaceLayout } from '../../shared/layout/useContentLayout';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketButton } from '../../shared/ticket/TicketButton'
import { TicketKpiStrip } from '../../shared/ticket/TicketKpiStrip'
import type { TicketKpiItem } from '../../shared/ticket/TicketKpiStrip'
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader'
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState'
import { NumericTextInput } from '../../shared/ui/NumericTextInput'
import { showError, showSuccess } from '../../shared/ui/appToast';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useScreenLoad } from '../../shared/ui/hooks-screen/useScreenLoad';
import { presentLoyaltyFailure, mapLoyaltyRemoteError } from '../application/mapLoyaltyRemoteError';
import {
  applyLoyaltyProgramRemote,
  fetchActiveLoyaltyProgram,
  fetchLoyaltyOrgSummary,
} from '../infra/loyaltyApi';
import type { LoyaltyOrgSummary, LoyaltyProgram } from '../domain/loyaltyTypes';

function newOpId(): string {
  return `loy-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function LoyaltyProgramScreen() {
  const canView = useModulePermission('Loyalty', 'view');
  const canEdit = useModulePermission('Loyalty', 'edit');
  const branchId = useSessionStore((s) => s.currentBranchId) ?? '';
  const { layoutWide } = useTabWorkspaceLayout();

  const [program, setProgram] = useState<LoyaltyProgram | null>(null);
  const [summary, setSummary] = useState<LoyaltyOrgSummary | null>(null);
  const [pointsPerArs, setPointsPerArs] = useState('100');
  const [redeemArs, setRedeemArs] = useState('1');
  const [minRedeem, setMinRedeem] = useState('1');
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [prog, sum] = await Promise.all([fetchActiveLoyaltyProgram(), fetchLoyaltyOrgSummary()]);
      setProgram(prog);
      setSummary(sum.ok ? sum : null);
      if (prog) {
        setPointsPerArs(String(prog.pointsPerArs));
        setRedeemArs(String(prog.redeemArsPerPoint));
        setMinRedeem(String(prog.minRedeemPoints));
      }
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'No pudimos cargar fidelización.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useScreenLoad('Loyalty', async () => {
    await load();
  });

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await load();
    } finally {
      setRefreshing(false);
    }
  }, [load]);

  const onSave = async () => {
    if (!canEdit || !branchId) return;
    const ppa = Number(pointsPerArs.replace(',', '.'));
    const rap = Number(redeemArs.replace(',', '.'));
    const min = Number(minRedeem.replace(',', '.'));
    if (!Number.isFinite(ppa) || ppa <= 0 || !Number.isFinite(rap) || rap <= 0 || !Number.isFinite(min) || min < 1) {
      showError('Revisá los números', 'Usá valores mayores a cero.');
      return;
    }
    setSaving(true);
    try {
      await applyLoyaltyProgramRemote(program?.id ? 'update' : 'insert', {
        id: program?.id ?? null,
        pointsPerArs: ppa,
        redeemArsPerPoint: rap,
        minRedeemPoints: Math.floor(min),
        isActive: true,
        branchId,
        operationId: newOpId(),
      });
      showSuccess('Programa guardado.');
      await load();
    } catch (e) {
      presentLoyaltyFailure({
        op: 'loyalty.program_save',
        error: e,
        userMessage: mapLoyaltyRemoteError(e instanceof Error ? e.message : null),
      });
    } finally {
      setSaving(false);
    }
  };

  const kpiItems = useMemo<TicketKpiItem[]>(
    () => [
      { label: 'Emitidos', value: `${summary?.earned ?? 0} pts`, testID: 'loyalty.kpi.earned' },
      { label: 'Canjeados', value: `${summary?.redeemed ?? 0} pts`, testID: 'loyalty.kpi.redeemed' },
      { label: 'Saldo', value: `${summary?.liabilityPoints ?? 0} pts`, testID: 'loyalty.kpi.liability' },
      { label: 'Cuentas', value: String(summary?.accounts ?? 0), testID: 'loyalty.kpi.accounts' },
    ],
    [summary]
  );

  if (!canView) {
    return (
      <ShellContentFrame style={ticketSurfaces.screen}>
        <ModuleEmptyState
          title="Sin acceso"
          subtitle="Pedile al dueño permiso para ver fidelización."
          testID="loyalty.no-permission"
        />
      </ShellContentFrame>
    );
  }

  return (
    <ShellContentFrame style={styles.container}>
      <View style={styles.listFlex} testID="loyalty.program.screen">
        <ScrollView
          style={styles.listFlex}
          contentContainerStyle={styles.scrollContent}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor={ticketColors.gold}
              colors={[ticketColors.gold]}
            />
          }
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.stack}>
            {/* Header Square */}
            <TicketModuleHeader
              kicker="Fidelización"
              title="Programa de puntos"
              meta="El cliente suma puntos al cobrar y puede canjearlos en el POS."
              testID="loyalty.header.sheet"
            />

            {/* KPIs */}
            <PaperSheet testID="loyalty.kpi.sheet">
              <TicketKpiStrip
                items={kpiItems}
                layout={layoutWide ? 'desk' : 'mobile'}
                variant="soft"
              />
            </PaperSheet>

            {/* Solo lectura banner */}
            {!canEdit ? (
              <View style={styles.readOnlyBanner}>
                <Text style={styles.readOnlyText}>
                  Solo lectura. Pedile permiso de edición al dueño.
                </Text>
              </View>
            ) : null}

            {/* Error banner */}
            {loadError ? (
              <View style={styles.errorBanner}>
                <Text style={styles.errorText}>{loadError}</Text>
              </View>
            ) : null}

            {/* Card: Configuración del programa */}
            <PaperSheet testID="loyalty.config.sheet" padded>
              <View style={styles.cardSection}>
                <Text style={styles.cardTitle}>Configuración</Text>
                <Text style={styles.cardCaption}>
                  El cliente gana 1 punto cada tantos pesos que compra. Al canjear, cada punto le descuenta tantos pesos.
                </Text>

                <View style={styles.inputsBlock}>
                  <View style={styles.formField}>
                    <Text style={styles.formFieldLabel}>Pesos para ganar 1 punto</Text>
                    <NumericTextInput
                      kind="decimalMoney"
                      style={styles.input}
                      value={pointsPerArs}
                      onChangeText={setPointsPerArs}
                      editable={canEdit && !saving}
                      testID="loyalty.points-per-ars"
                    />
                  </View>

                  <View style={styles.formField}>
                    <Text style={styles.formFieldLabel}>Descuento en pesos por punto</Text>
                    <NumericTextInput
                      kind="decimalMoney"
                      style={styles.input}
                      value={redeemArs}
                      onChangeText={setRedeemArs}
                      editable={canEdit && !saving}
                      testID="loyalty.redeem-ars"
                    />
                  </View>

                  <View style={styles.formField}>
                    <Text style={styles.formFieldLabel}>Mínimo de puntos para canjear</Text>
                    <NumericTextInput
                      kind="decimalMoney"
                      style={styles.input}
                      value={minRedeem}
                      onChangeText={setMinRedeem}
                      editable={canEdit && !saving}
                      testID="loyalty.min-redeem"
                    />
                  </View>
                </View>

                {canEdit ? (
                  <View style={styles.saveWrap}>
                    <TicketButton
                      variant="gold"
                      label={saving ? 'Guardando…' : 'Guardar programa'}
                      onPress={() => void onSave()}
                      disabled={saving}
                      testID="loyalty.save"
                    />
                  </View>
                ) : null}
              </View>
            </PaperSheet>
          </View>
        </ScrollView>
      </View>
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: ticketColors.bg,
  },
  listFlex: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    paddingBottom: spacing.xl,
  },
  stack: {
    gap: 10,
  },
  cardSection: {
    gap: spacing.md,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  cardCaption: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    lineHeight: 18,
  },
  inputsBlock: {
    gap: spacing.md,
  },
  formField: {
    gap: spacing.xs,
  },
  formFieldLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  input: {
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    fontSize: 15,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  saveWrap: {
    marginTop: spacing.sm,
  },
  readOnlyBanner: {
    backgroundColor: ticketColors.paper,
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  readOnlyText: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    fontWeight: '600',
  },
  errorBanner: {
    padding: spacing.md,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.bad,
    backgroundColor: ticketColors.paper,
  },
  errorText: {
    color: ticketColors.bad,
    lineHeight: 20,
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
  },
});
